export interface ProgressSegment {
  value: number
  color: string
  label?: string
}

export interface ProgressBarProps {
  value?: number
  max?: number
  percentage?: number
  color?: string
  segments?: ProgressSegment[]
  trackColor?: string
  height?: string
  rounded?: string
  className?: string
  showLabels?: boolean
}

export function ProgressBar({
  value,
  max = 100,
  percentage,
  color = '#146971',
  segments,
  trackColor = 'bg-[#e5e7eb]',
  height = 'h-2',
  rounded = 'rounded-full',
  className = '',
}: ProgressBarProps) {
  // Multi-segmented mode
  if (segments && segments.length > 0) {
    const total = max > 0 ? max : segments.reduce((acc, seg) => acc + (seg.value || 0), 0) || 100

    return (
      <div
        className={`w-full overflow-hidden ${trackColor} ${height} ${rounded} flex ${className}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
      >
        {segments.map((seg, idx) => {
          const segPercent = Math.max(0, Math.min(100, ((seg.value || 0) / total) * 100))
          if (segPercent <= 0) return null

          const isCustomHex = seg.color.startsWith('#') || seg.color.startsWith('rgb')
          return (
            <div
              key={idx}
              title={seg.label ? `${seg.label}: ${seg.value} (${segPercent.toFixed(1)}%)` : undefined}
              className={`h-full transition-all duration-300 ${isCustomHex ? '' : seg.color}`}
              style={{
                width: `${segPercent}%`,
                ...(isCustomHex ? { backgroundColor: seg.color } : {}),
              }}
            />
          )
        })}
      </div>
    )
  }

  // Single value mode
  const calcPercent =
    percentage !== undefined
      ? Math.max(0, Math.min(100, percentage))
      : value !== undefined
        ? Math.max(0, Math.min(100, (value / (max || 100)) * 100))
        : 0

  const isCustomHex = color.startsWith('#') || color.startsWith('rgb')

  return (
    <div
      className={`w-full overflow-hidden ${trackColor} ${height} ${rounded} ${className}`}
      role="progressbar"
      aria-valuenow={value ?? calcPercent}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div
        className={`h-full transition-all duration-300 ${rounded} ${isCustomHex ? '' : color}`}
        style={{
          width: `${calcPercent}%`,
          ...(isCustomHex ? { backgroundColor: color } : {}),
        }}
      />
    </div>
  )
}
